import { ArrowForward } from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  Button,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import './CreateEmail.css'
import { useState } from 'react'
import axios from 'axios'
import { salesOrderLineType, salesOrderType, trackingType } from '../types'
import dayjs from 'dayjs'
import { formatAddress } from '../helper'

function CreateEmail(props: {
  order: salesOrderType
  lineItems: salesOrderLineType[]
  trackingNumbers: trackingType[]
  salesRep: { rep_code: string; sales_rep_phone: string; sales_rep_email: string }
  supportEmails: string[]
  isVIT: boolean
  setOpen: (open: boolean) => void
}) {
  const emailTemplates = new Map([
    [
      'Order Completed',
      {
        subject: `Your Order Number: ${props.order.ref_number} has been completed`,
        body: `Dear Customer,\n\nThis email notification is to inform you that your order has been completed.`,
      },
    ],
    [
      'Order Processed',
      {
        subject: `Your Order Number: ${props.order.ref_number} is being processed`,
        body: `Dear Customer,\n\nWe have received your order and are processing it now. Should you have any questions or concerns, please let us know.`,
      },
    ],
    [
      'Order Shipped',
      {
        subject: `The following item(s) in your Order number: ${props.order.ref_number} is being shipped`,
        body: `Dear Customer,\n\nThis email notification is to notify you that the following has shipped.`,
      },
    ],
    [
      'Order Has New ETA Date',
      {
        subject: `The following item(s) in your Order Number: ${props.order.ref_number} has a new ETA date`,
        body: `Dear Customer,\n\nFrom time-to-time projected ship dates change and this has affected your order.`,
      },
    ],
  ])

  const [to, setTo] = useState(props.supportEmails.join('; '))
  const [cc, setCc] = useState(
    (process.env.REACT_APP_OPS_EMAIL ?? '') + (props.isVIT ? '; ' + (process.env.REACT_APP_VIT_EMAIL ?? '') : ''),
  )
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [template, setTemplate] = useState('')
  const [emailStatus, setEmailStatus] = useState('')

  const handleTemplateChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newTemplate = event.target.value as string
    setTemplate(newTemplate)
    setSubject(emailTemplates.get(newTemplate)?.subject || '')
    setBody(emailTemplates.get(newTemplate)?.body || '')
  }

  const handleSendEmail = () => {
    axios
      .post('/sendEmail', {
        to: to.split(';'),
        cc: cc ? cc.split(';') : [],
        subject: subject,
        body:
          body.replaceAll('\n', '<br/>') +
          `
        <br /><br />
        Please reference your purchase information below:<br /><br />
        <b>Purchase Order Number:</b> ${props.order.customer_po_number}<br />
        <b>Quote Number:</b> ${props.order.quote_works_number}<br />
        <b>Purchase Order Date:</b> ${dayjs.utc(props.order.txn_date).format('MM/DD/YYYY')}<br /><br />
        <table style="border: 1px solid black; border-collapse: collapse; width: 100%;">
          <tr>
            <td colspan="8" style="text-align: center;"><b>***ORDER DETAILS***</b></td>
          </tr>
          ${
            template === 'Order Processed'
              ? `
            <tr>
              <td style="border: 1px solid black; text-align: center;"><b>ITEM DESCRIPTION</b></td>
              <td style="border: 1px solid black; text-align: center;"><b>ITEM SKU</b></td>
              <td style="border: 1px solid black; text-align: center;"><b>QUANTITY</b></td>
            </tr>
            ${props.lineItems
              .map(item => {
                return `
              <tr>
                <td style="border: 1px solid black;">${
                  (item.item_desc?.indexOf('\n') ?? 0) > 0
                    ? item.item_desc?.substring(0, Math.min(item.item_desc.indexOf('\n'), 100))
                    : item.item_desc?.substring(0, 100)
                }</td>
                <td style="border: 1px solid black;">${item.item_name ?? ''}</td>
                <td style="border: 1px solid black;">${item.quantity ?? ''}</td>
              </tr>
            `
              })
              .join('')}
            `
              : template === 'Order Completed'
              ? `
            <tr>
              <td style="border: 1px solid black; text-align: center;"><b>ITEM DESCRIPTION</b></td>
              <td style="border: 1px solid black; text-align: center;"><b>ITEM SKU</b></td>
              <td style="border: 1px solid black; text-align: center;"><b>QUANTITY</b></td>
              <td style="border: 1px solid black; text-align: center;"><b>TRACKING NUMBERS</b></td>
              <td style="border: 1px solid black; text-align: center;"><b>PERSON SIGNED</b></td>
            </tr>
            ${props.lineItems
              .map(item => {
                return `
              <tr>
                <td style="border: 1px solid black;">${
                  (item.item_desc?.indexOf('\n') ?? 0) > 0
                    ? item.item_desc?.substring(0, Math.min(item.item_desc.indexOf('\n'), 100))
                    : item.item_desc?.substring(0, 100)
                }</td>
                <td style="border: 1px solid black;">${item.item_name ?? ''}</td>
                <td style="border: 1px solid black;">${item.quantity ?? ''}</td>
                <td style="border: 1px solid black;">${
                  props.trackingNumbers
                    .filter(t => t.sales_order_line_txn_id === item.sales_order_line_txn_id)
                    .map(t => t.tracking_number)
                    .join(', ') || '-'
                }</td>
                <td style="border: 1px solid black;">${
                  props.trackingNumbers
                    .filter(t => t.sales_order_line_txn_id === item.sales_order_line_txn_id && t.person_signed)
                    .map(t => t.person_signed)
                    .join(', ') || '-'
                }</td>
              </tr>
            `
              })
              .join('')}
            `
              : template === 'Order Shipped'
              ? `
          <tr>
            <td style="border: 1px solid black; text-align: center;"><b>ITEM DESCRIPTION</b></td>
            <td style="border: 1px solid black; text-align: center;"><b>ITEM SKU</b></td>
            <td style="border: 1px solid black; text-align: center;"><b>QUANTITY ORDERED</b></td>
            <td style="border: 1px solid black; text-align: center;"><b>QUANTITY SHIPPED</b></td>
            <td style="border: 1px solid black; text-align: center;"><b>CARRIER</b></td>
            <td style="border: 1px solid black; text-align: center;"><b>TRACKING NUMBERS</b></td>
            <td style="border: 1px solid black; text-align: center;"><b>SHIP DATE</b></td>
            <td style="border: 1px solid black; text-align: center;"><b>ETA DATE</b></td>
            ${
              !props.order.ship_address_addr1
                ? `<td style="border: 1px solid black; text-align: center;"><b>SHIP TO ADDRESS</b></td>`
                : ''
            }
          </tr>
          ${props.lineItems
            .map(item => {
              const trackingNumber = props.trackingNumbers.filter(
                tracking => tracking.sales_order_line_txn_id === item.sales_order_line_txn_id,
              )
              return `
            <tr>
              <td style="border: 1px solid black;">${
                (item.item_desc?.indexOf('\n') ?? 0) > 0
                  ? item.item_desc?.substring(0, Math.min(item.item_desc.indexOf('\n'), 100))
                  : item.item_desc?.substring(0, 100)
              }</td>
              <td style="border: 1px solid black;">${item.item_name ?? ''}</td>
              <td style="border: 1px solid black;">${item.quantity ?? ''}</td>
              <td style="border: 1px solid black;">${item.total_shipped_quantity ?? ''}</td>
              <td style="border: 1px solid black;">${Array.from(
                new Set(trackingNumber.map(t => t.ship_method).filter(t => t)),
              ).join(', ')}</td>
              <td style="border: 1px solid black;">${trackingNumber.map(t => t.tracking_number).join(', ')}</td>
              <td style="border: 1px solid black;">${Array.from(
                new Set(trackingNumber.filter(t => t.ship_date).map(t => dayjs.utc(t.ship_date).format('MM/DD/YYYY'))),
              ).join(', ')}</td>
              <td style="border: 1px solid black;">${Array.from(
                new Set(
                  trackingNumber.filter(t => t.delivery_date).map(t => dayjs.utc(t.delivery_date).format('MM/DD/YYYY')),
                ),
              ).join(', ')}</td>
              ${
                !props.order.ship_address_addr1
                  ? `<td style="border: 1px solid black;">${formatAddress(item).replaceAll('\n', '<br />')}</td>`
                  : ''
              }
            </tr>
          `
            })
            .join('')}
          `
              : template === 'Order Has New ETA Date'
              ? `
          <tr>
            <td style="border: 1px solid black; text-align: center;"><b>ITEM DESCRIPTION</b></td>
            <td style="border: 1px solid black; text-align: center;"><b>ITEM SKU</b></td>
            <td style="border: 1px solid black; text-align: center;"><b>QUANTITY ORDERED</b></td>
            <td style="border: 1px solid black; text-align: center;"><b>QUANTITY SHIPPED</b></td>
            <td style="border: 1px solid black; text-align: center;"><b>PREVIOUS ETA DATE</b></td>
            <td style="border: 1px solid black; text-align: center;"><b>REVISED ETA DATE</b></td>
          </tr>
          ${props.lineItems
            .map(item => {
              return `
            <tr>
              <td style="border: 1px solid black;">${
                (item.item_desc?.indexOf('\n') ?? 0) > 0
                  ? item.item_desc?.substring(0, Math.min(item.item_desc.indexOf('\n'), 100))
                  : item.item_desc?.substring(0, 100)
              }</td>
              <td style="border: 1px solid black;">${item.item_name ?? ''}</td>
              <td style="border: 1px solid black;">${item.quantity ?? ''}</td>
              <td style="border: 1px solid black;">${item.total_shipped_quantity ?? ''}</td>
              <td style="border: 1px solid black;">${
                item.eta_date_previous ? dayjs.utc(item.eta_date_previous).format('MM/DD/YYYY') : ''
              }</td>
              <td style="border: 1px solid black;">${
                item.eta_date_current ? dayjs.utc(item.eta_date_current).format('MM/DD/YYYY') : ''
              }</td>
            </tr>
            `
            })
            .join('')}
          </tr>`
              : ''
          }
        </table>
        <br /><br />
        ${
          props.order.ship_address_addr1
            ? `<table style="border: 1px solid black; border-collapse: collapse; width: 100%;">
          <tr><td colspan="2" style="border: 1px solid black; text-align: center;"><b>***SHIPPING DETAILS***</b></td></tr>
          <tr>
            <td style="border: 1px solid black"><b>SHIP TO ADDRESS</b></td>
            <td style="border: 1px solid black">${
              props.order.ship_address_attention ? props.order.ship_address_attention + '<br />' : ''
            }
            ${props.order.ship_address_addressee ? props.order.ship_address_addressee + '<br />' : ''}
            ${props.order.ship_address_addr1 ? props.order.ship_address_addr1 + '<br />' : ''}
            ${props.order.ship_address_addr2 ? props.order.ship_address_addr2 + '<br />' : ''}
            ${props.order.ship_address_addr3 ? props.order.ship_address_addr3 + '<br />' : ''}
            ${props.order.ship_address_addr4 ? props.order.ship_address_addr4 + '<br />' : ''}
            ${props.order.ship_address_city}, ${props.order.ship_address_state} ${props.order.ship_address_zip}
            </td>
          </tr>
        </table>

        <br /><br />`
            : ''
        }
        <b>We appreciate your business as a valued customer and look forward to working with you in the near future. Thank you once again for your order.</b>

        <br /><br />
        <b>${props.salesRep.rep_code}</b>
        ${props.salesRep.sales_rep_phone ? `<br />${props.salesRep.sales_rep_phone}` : ''}
        ${props.salesRep.sales_rep_email ? `<br />${props.salesRep.sales_rep_email}` : ''}
        `,
      })
      .then(() => {
        setEmailStatus('success')
      })
      .catch(() => {
        setEmailStatus('error')
      })
  }

  return (
    <div className="create-email">
      <div className="create-email-header">
        <div>
          <Typography variant="h6">Create E-mail</Typography>
          <IconButton onClick={() => props.setOpen(false)}>
            <ArrowForward />
          </IconButton>
        </div>
        <Divider />
      </div>
      <div className="create-email-body">
        <Grid container spacing="17px">
          <Grid size={4}>
            <TextField
              select
              fullWidth
              size="small"
              label="Select Template"
              value={template}
              onChange={handleTemplateChange}
            >
              {Array.from(emailTemplates.keys()).map(template => (
                <MenuItem key={template} value={template}>
                  {template}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={8} />
          <Grid size={2}>
            <Typography>To:</Typography>
          </Grid>
          <Grid size={10}>
            <TextField size="small" fullWidth multiline value={to} onChange={e => setTo(e.target.value)} />
          </Grid>
          <Grid size={2}>
            <Typography>CC:</Typography>
          </Grid>
          <Grid size={10}>
            <TextField size="small" fullWidth multiline value={cc} onChange={e => setCc(e.target.value)} />
          </Grid>
          <Grid size={2}>
            <Typography>Subject:</Typography>
          </Grid>
          <Grid size={10}>
            <TextField size="small" fullWidth multiline value={subject} onChange={e => setSubject(e.target.value)} />
          </Grid>
          <Grid size={2}>
            <Typography>Email Body:</Typography>
          </Grid>
          <Grid size={10}>
            <TextField
              size="small"
              rows={10}
              multiline
              fullWidth
              value={body}
              onChange={e => setBody(e.target.value)}
            />
          </Grid>
        </Grid>
        <Button
          size="large"
          variant="contained"
          disabled={!to || !subject || !body}
          onClick={handleSendEmail}
          loading={emailStatus === 'sending'}
        >
          Send E-mail
        </Button>
        {emailStatus === 'error' && (
          <Alert severity="error">
            <AlertTitle>Email Delivery Failed</AlertTitle>
            Please try again. If the problem persists, please contact support.
          </Alert>
        )}
        <div className="create-email-purchase-info">
          <Typography>Please reference your purchase information below:</Typography>
          <div className="create-email-purchase-info-panel1">
            <div>
              <Typography color="textSecondary" fontSize="14px">
                Purchase Order Number:
              </Typography>
              <Typography>{props.order.customer_po_number}</Typography>
            </div>
            <div>
              <Typography color="textSecondary" fontSize="14px">
                Quote Number:
              </Typography>
              <Typography>{props.order.quote_works_number}</Typography>
            </div>
            <div>
              <Typography color="textSecondary" fontSize="14px">
                Purchase Order Date:
              </Typography>
              <Typography>{dayjs.utc(props.order.txn_date).format('MM/DD/YYYY')}</Typography>
            </div>
          </div>
          <TableContainer className="create-email-purchase-info-panel2">
            <Table color="secondary">
              <TableHead>
                <TableRow>
                  <TableCell width="45%">Item Description</TableCell>
                  <TableCell width="45%">Item SKU</TableCell>
                  <TableCell width="10%" align="right">
                    Quantity
                  </TableCell>
                  {template === 'Order Completed' && <TableCell width="20%">Person Signed</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {props.lineItems.map(item => (
                  <TableRow key={item.sales_order_line_txn_id}>
                    <TableCell>
                      {item.item_desc?.length
                        ? item.item_desc.length > 50
                          ? item.item_desc.substring(0, 50) + '...'
                          : item.item_desc
                        : ''}
                    </TableCell>
                    <TableCell>{item.item_name}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    {template === 'Order Completed' && (
                      <TableCell>
                        {props.trackingNumbers
                          .filter(t => t.sales_order_line_txn_id === item.sales_order_line_txn_id && t.person_signed)
                          .map(t => t.person_signed)
                          .join(', ') || ''}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Grid container spacing="16px">
            <Grid size={4} className="create-email-purchase-info-panel3-column">
              <Typography color="textSecondary" fontSize="14px">
                Ship to Address:
              </Typography>
              <Typography>
                {props.order.ship_address_attention ? (
                  <>
                    {props.order.ship_address_attention}
                    <br />
                  </>
                ) : (
                  ''
                )}
                {props.order.ship_address_addressee ? (
                  <>
                    {props.order.ship_address_addressee}
                    <br />
                  </>
                ) : (
                  ''
                )}
                {props.order.ship_address_addr1}
                <br />
                {props.order.ship_address_addr2 ? (
                  <>
                    {props.order.ship_address_addr2}
                    <br />
                  </>
                ) : (
                  ''
                )}
                {props.order.ship_address_addr3 ? (
                  <>
                    {props.order.ship_address_addr3}
                    <br />
                  </>
                ) : (
                  ''
                )}
                {props.order.ship_address_addr4 ? (
                  <>
                    {props.order.ship_address_addr4}
                    <br />
                  </>
                ) : (
                  ''
                )}
                {props.order.ship_address_city}, {props.order.ship_address_state} {props.order.ship_address_zip}
              </Typography>
            </Grid>
            <Grid size={4} className="create-email-purchase-info-panel3-column">
              <Typography color="textSecondary" fontSize="14px">
                Message:
              </Typography>
              <Typography>
                We appreciate your business as a valued customer and look forward to working with you in the near
                future. Thank you once again for your order!
              </Typography>
            </Grid>
            <Grid size={4} className="create-email-purchase-info-panel3-column">
              <Typography color="textSecondary" fontSize="14px">
                Sales Representatives:
              </Typography>
              <Typography>
                {props.salesRep.rep_code}
                <br />
                {props.salesRep.sales_rep_phone ? (
                  <>
                    {props.salesRep.sales_rep_phone}
                    <br />
                  </>
                ) : null}
                {props.salesRep.sales_rep_email}
              </Typography>
            </Grid>
          </Grid>
          <Snackbar
            open={emailStatus === 'success'}
            autoHideDuration={6000}
            onClose={() => setEmailStatus('')}
            message="Email Sent"
          />
        </div>
      </div>
    </div>
  )
}

export default CreateEmail
